/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * Typographic-quote diagnosis for pasted JSON.
 *
 * JSON accepts only the straight ASCII double quote (`"`). Text pasted from a
 * word processor, notes app, email client, or chat surface is frequently
 * "smart-quoted" — the app silently rewrites `"` to a curly pair (`“ ”`). The
 * result parses to a bare `Expected property name` error at the first quote,
 * which tells the operator nothing about the real cause.
 *
 * These helpers turn that into a diagnosable, one-click fix (source-discovery
 * design R3 — a failure leads with a diagnosis, not the raw parser message).
 * They deliberately touch **only the double-quote family**: those are the
 * characters JSON uses structurally, so replacing them is the whole fix. Smart
 * apostrophes (`’`) are left alone — they are valid JSON string content and
 * rewriting them would silently alter an operator's data.
 *
 * The transform is never applied silently. Callers detect with
 * {@link hasTypographicDoubleQuotes}, offer the normalized text for the
 * operator to see, and re-parse only on an explicit action.
 */

/**
 * Typographic double-quote characters an autocorrect pass emits in place of a
 * straight `"`. Guillemets (`« »`) are intentionally excluded — they are
 * legitimate content in several languages and are not what quote-autocorrect
 * produces.
 */
const TYPOGRAPHIC_DOUBLE_QUOTES = [
  '“', // “ LEFT DOUBLE QUOTATION MARK
  '”', // ” RIGHT DOUBLE QUOTATION MARK
  '„', // „ DOUBLE LOW-9 QUOTATION MARK
  '‟', // ‟ DOUBLE HIGH-REVERSED-9 QUOTATION MARK
  '″', // ″ DOUBLE PRIME
  '‶', // ‶ REVERSED DOUBLE PRIME
  '〝', // 〝 REVERSED DOUBLE PRIME QUOTATION MARK
  '〞', // 〞 DOUBLE PRIME QUOTATION MARK
  '＂', // ＂ FULLWIDTH QUOTATION MARK
];

const TYPOGRAPHIC_DOUBLE_QUOTE_RE = new RegExp(`[${TYPOGRAPHIC_DOUBLE_QUOTES.join('')}]`, 'g');

/** True when the text contains any typographic double quote a paste may have introduced. */
export function hasTypographicDoubleQuotes(text: string): boolean {
  TYPOGRAPHIC_DOUBLE_QUOTE_RE.lastIndex = 0;
  return TYPOGRAPHIC_DOUBLE_QUOTE_RE.test(text);
}

/** Replace every typographic double quote with a straight ASCII `"`. */
export function normalizeTypographicQuotes(text: string): string {
  return text.replace(TYPOGRAPHIC_DOUBLE_QUOTE_RE, '"');
}

/**
 * Given text that failed `JSON.parse`, decide whether smart quotes are the
 * likely cause and, if so, whether normalizing them actually yields parseable
 * JSON. Returns the fix only when it demonstrably works, so a caller never
 * offers a "Fix quotes" affordance that wouldn't help.
 */
export function diagnoseTypographicQuotes(text: string): { fixable: boolean; normalized: string } {
  if (!hasTypographicDoubleQuotes(text)) return { fixable: false, normalized: text };
  const normalized = normalizeTypographicQuotes(text);
  try {
    JSON.parse(normalized);
    return { fixable: true, normalized };
  } catch {
    return { fixable: false, normalized };
  }
}
